import { c as _c } from "react/compiler-runtime";
import { match } from 'ts-pattern';
import { memo } from 'react';
import { COLUMN_SEPARATOR_STYLE, PINNED_LEFT_EDGE_LINE_STYLE, PINNED_RIGHT_EDGE_LINE_STYLE, RESIZE_INDICATOR_LEFT_STYLE, RESIZE_INDICATOR_STYLE } from '../../../../../../constants/borderStyles';
import { DEFAULT_COL_WIDTH } from '../../../../../../constants/defaultColWidth';
import { selectCellOverlayState } from '../../../../../../store/selectors/selectCellOverlayState';
import { useEagGridSelector } from '../../../../../../store/useEagGridSelector';
import type { EagColumn } from '../../../../../../types/EagColumn';
import type { EagColumnState } from '../../../../../../types/EagColumnState';
import type { RowData } from '../../../../../../types/RowData';
import { getValueFromRawCell } from '../../../../../../utils/getValueFromRawCell';
import { getRowPaddingClass, getRowVerticalPaddingPx, LineHeightToPx, type LineHeight } from '../../../../../../utils/lineHeightToValue';
import { renderCell } from '../../../../../../utils/renderCell';

// Drag/resize state is read via a single composite selector (selectCellOverlayState)
// instead of 5 individual selectors.  This cuts subscriptions from 5 × N×M to 1 × N×M.
// verticalSeparators, valueWrapMode, and rowHeight are passed as props from GridRow
// (same for all cells in a row) to avoid per-cell store subscriptions for rarely-changing settings.

export type GridCellProps = {
  column: EagColumn;
  columnState: EagColumnState;
  row: RowData;
  pinnedLeftOffset?: number;
  pinnedRightOffset?: number;
  verticalSeparators: boolean;
  valueWrapMode: string;
  rowHeight: LineHeight;
  pinnedEdgeShadowClass?: string;
  /** Suppress the right-edge column separator on the last data column when an
   * actions column immediately follows — the actions column already renders its
   * own left-edge border, so the two would otherwise form a double line. */
  isLastBeforeActions?: boolean;
};

// memo: rendered N×M times (rows × columns); prevents cascading re-renders from parent.
export const GridCell = memo(props => {
  const $ = _c(68);
  const overlay = useEagGridSelector(selectCellOverlayState);
  const rawValue = props.row[props.column.field];
  let isBeingResized;
  let isDropLeft;
  let isDropRight;
  let isPinned;
  let t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== overlay.activeDragId || $[1] !== overlay.dropSide || $[2] !== overlay.isResizing || $[3] !== overlay.overColumnId || $[4] !== overlay.resizingColumn || $[5] !== props.column || $[6] !== props.columnState.pinned || $[7] !== props.columnState.width || $[8] !== props.pinnedEdgeShadowClass || $[9] !== props.pinnedLeftOffset || $[10] !== props.pinnedRightOffset || $[11] !== props.row || $[12] !== props.rowHeight || $[13] !== props.valueWrapMode || $[14] !== rawValue) {
    const value = getValueFromRawCell(props.column, rawValue);
    const width = props.columnState.width ?? DEFAULT_COL_WIDTH;
    let t4;
    if ($[23] !== props.columnState.pinned || $[24] !== props.pinnedLeftOffset || $[25] !== props.pinnedRightOffset) {
      let t5;
      if ($[27] !== props.pinnedLeftOffset) {
        t5 = () => ({
          position: "sticky" as const,
          left: props.pinnedLeftOffset ?? 0,
          zIndex: "var(--z-sidebar)"
        });
        $[27] = props.pinnedLeftOffset;
        $[28] = t5;
      } else {
        t5 = $[28];
      }
      let t6;
      if ($[29] !== props.pinnedRightOffset) {
        t6 = () => ({
          position: "sticky" as const,
          right: props.pinnedRightOffset ?? 0,
          zIndex: "var(--z-sidebar)"
        });
        $[29] = props.pinnedRightOffset;
        $[30] = t6;
      } else {
        t6 = $[30];
      }
      t4 = match(props.columnState.pinned).with("LEFT", t5).with("RIGHT", t6).otherwise(_temp);
      $[23] = props.columnState.pinned;
      $[24] = props.pinnedLeftOffset;
      $[25] = props.pinnedRightOffset;
      $[26] = t4;
    } else {
      t4 = $[26];
    }
    const pinnedStyle = t4;
    isPinned = props.columnState.pinned === "LEFT" || props.columnState.pinned === "RIGHT";
    isBeingResized = overlay.isResizing && overlay.resizingColumn === props.column.key;
    const isDragSource = overlay.activeDragId === props.column.key;
    const isDropTarget = overlay.overColumnId === props.column.key && !isDragSource;
    const dragOpacity = isDragSource ? 0.3 : 1;
    isDropLeft = isDropTarget && overlay.dropSide === "left";
    isDropRight = isDropTarget && overlay.dropSide === "right";
    let t5;
    if ($[31] !== isDropTarget || $[32] !== isPinned) {
      t5 = match({
        isPinned,
        isDropTarget
      }).with({
        isDropTarget: true
      }, _temp2).with({
        isPinned: true
      }, _temp3).otherwise(_temp4);
      $[31] = isDropTarget;
      $[32] = isPinned;
      $[33] = t5;
    } else {
      t5 = $[33];
    }
    const bgClass = t5;
    const wrapClass = props.valueWrapMode === "WRAP" ? "whitespace-normal break-words" : "text-ellipsis whitespace-nowrap";
    const paddingClass = getRowPaddingClass[props.rowHeight];
    const rowHeightPx = LineHeightToPx[props.rowHeight];
    const thumbnailHeightPx = rowHeightPx - getRowVerticalPaddingPx[props.rowHeight];
    const shadowClass = props.pinnedEdgeShadowClass ?? "";
    t0 = `relative overflow-hidden ${paddingClass} ${wrapClass} ${bgClass} ${shadowClass}`;
    if ($[34] !== dragOpacity || $[35] !== pinnedStyle || $[36] !== rowHeightPx || $[37] !== width) {
      t1 = {
        width,
        maxWidth: width,
        height: rowHeightPx,
        maxHeight: rowHeightPx,
        ...pinnedStyle,
        opacity: dragOpacity
      };
      $[34] = dragOpacity;
      $[35] = pinnedStyle;
      $[36] = rowHeightPx;
      $[37] = width;
      $[38] = t1;
    } else {
      t1 = $[38];
    }
    t2 = props.column.key;
    t3 = renderCell(props.column, value, props.row, thumbnailHeightPx);
    $[0] = overlay.activeDragId;
    $[1] = overlay.dropSide;
    $[2] = overlay.isResizing;
    $[3] = overlay.overColumnId;
    $[4] = overlay.resizingColumn;
    $[5] = props.column;
    $[6] = props.columnState.pinned;
    $[7] = props.columnState.width;
    $[8] = props.pinnedEdgeShadowClass;
    $[9] = props.pinnedLeftOffset;
    $[10] = props.pinnedRightOffset;
    $[11] = props.row;
    $[12] = props.rowHeight;
    $[13] = props.valueWrapMode;
    $[14] = rawValue;
    $[15] = isBeingResized;
    $[16] = isDropLeft;
    $[17] = isDropRight;
    $[18] = isPinned;
    $[19] = t0;
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
  } else {
    isBeingResized = $[15];
    isDropLeft = $[16];
    isDropRight = $[17];
    isPinned = $[18];
    t0 = $[19];
    t1 = $[20];
    t2 = $[21];
    t3 = $[22];
  }
  let t4;
  if ($[39] !== isBeingResized || $[40] !== props.columnState.pinned) {
    t4 = isBeingResized && <span style={props.columnState.pinned === "RIGHT" ? RESIZE_INDICATOR_LEFT_STYLE : RESIZE_INDICATOR_STYLE} />;
    $[39] = isBeingResized;
    $[40] = props.columnState.pinned;
    $[41] = t4;
  } else {
    t4 = $[41];
  }
  let t5;
  if ($[42] !== isBeingResized || $[43] !== props.columnState.pinned) {
    t5 = !isBeingResized && props.columnState.pinned === "LEFT" && <span style={PINNED_LEFT_EDGE_LINE_STYLE} />;
    $[42] = isBeingResized;
    $[43] = props.columnState.pinned;
    $[44] = t5;
  } else {
    t5 = $[44];
  }
  let t6;
  if ($[45] !== isBeingResized || $[46] !== props.columnState.pinned) {
    t6 = !isBeingResized && props.columnState.pinned === "RIGHT" && <span style={PINNED_RIGHT_EDGE_LINE_STYLE} />;
    $[45] = isBeingResized;
    $[46] = props.columnState.pinned;
    $[47] = t6;
  } else {
    t6 = $[47];
  }
  let t7;
  if ($[48] !== isBeingResized || $[49] !== isPinned || $[50] !== props.isLastBeforeActions || $[51] !== props.verticalSeparators) {
    t7 = !isBeingResized && props.verticalSeparators && !isPinned && !props.isLastBeforeActions && <span style={COLUMN_SEPARATOR_STYLE} />;
    $[48] = isBeingResized;
    $[49] = isPinned;
    $[50] = props.isLastBeforeActions;
    $[51] = props.verticalSeparators;
    $[52] = t7;
  } else {
    t7 = $[52];
  }
  let t8;
  if ($[53] !== isDropLeft) {
    t8 = isDropLeft && <span className="absolute left-0 top-0 h-full w-px bg-blue-500 z-(--z-page-navigation)" />;
    $[53] = isDropLeft;
    $[54] = t8;
  } else {
    t8 = $[54];
  }
  let t9;
  if ($[55] !== isDropRight) {
    t9 = isDropRight && <span className="absolute right-0 top-0 h-full w-px bg-blue-500 z-(--z-page-navigation)" />;
    $[55] = isDropRight;
    $[56] = t9;
  } else {
    t9 = $[56];
  }
  let t10;
  if ($[57] !== t0 || $[58] !== t1 || $[59] !== t2 || $[60] !== t3 || $[61] !== t4 || $[62] !== t5 || $[63] !== t6 || $[64] !== t7 || $[65] !== t8 || $[66] !== t9) {
    t10 = <td className={t0} style={t1} data-column={t2}>{t3}{t4}{t5}{t6}{t7}{t8}{t9}</td>;
    $[57] = t0;
    $[58] = t1;
    $[59] = t2;
    $[60] = t3;
    $[61] = t4;
    $[62] = t5;
    $[63] = t6;
    $[64] = t7;
    $[65] = t8;
    $[66] = t9;
    $[67] = t10;
  } else {
    t10 = $[67];
  }
  return t10;
});
function _temp() {}
function _temp2() {
  return "bg-blue-50";
}
function _temp3() {
  return "bg-white group-hover/row:bg-slate-50";
}
function _temp4() {
  return "bg-inherit";
}